import React, { useState, useEffect, useCallback } from "react";
import { Button, Form, InputGroup } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { useParams } from "react-router-dom";

import { resetPasswordCheckUser, resetPasswordConfirmUser } from "../../../redux/slices/userSlice";
import { showAlert } from "../../../redux/slices/alertSlice";

import "../../../assets/css/forms.css";
import { EyeIcon, EyeSlashIcon } from "../../../components/Icon/icons";
import IconButton from "../../../components/IconButton/IconButton";
import AuthenticationLayout from "../../../components/UI/AuthenticationLayout/AuthenticationLayout";
import ButtonSpinner from "../../../components/UI/ButtonSpinner/ButtonSpinner";
import ContainerSpinner from "../../../components/UI/ContainerSpinner/ContainerSpinner";
import FormAlert from "../../../components/UI/FormAlert/FormAlert";

function PasswordResetConfirmScreen() {
  const { uid, token } = useParams();

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isPasswordShown, setIsPasswordShown] = useState(false);

  const [newPassword, setNewPassword] = useState("");
  const [reNewPassword, setReNewPassword] = useState("");

  const dispatch = useDispatch();
  const userSlice = useSelector((state) => state.userSlice);

  const navigate = useNavigate();
  const goToHome = useCallback(() => {
    navigate("/");
  }, [navigate]);

  const handleShowAlert = useCallback(
    (alert) => {
      dispatch(showAlert(alert));
    },
    [dispatch]
  );

  const onChangeIsPasswordShown = () => {
    setIsPasswordShown(!isPasswordShown);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    dispatch(resetPasswordConfirmUser({ uid, token, newPassword, reNewPassword }));
    setIsSubmitted(true);
  };

  useEffect(() => {
    const canRedirect = isSubmitted && !userSlice.loading && !userSlice.error;
    if (canRedirect) {
      handleShowAlert({
        headingText: "Senha redefinida!",
        text: "Senha redefinida com sucesso!",
      });
      goToHome();
    }
  }, [goToHome, handleShowAlert, isSubmitted, userSlice.error, userSlice.loading]);

  useEffect(() => {
    if (!isSubmitted) {
      dispatch(resetPasswordCheckUser({ uid, token }));
    }
  }, [dispatch, isSubmitted, token, uid]);

  return (
    <>
      <Helmet>
        <title>Redefinir Senha</title>
        <link rel="stylesheet" href="/bootstrap.alt.css" />
      </Helmet>

      <AuthenticationLayout>
        <div className="authentication-form">
          {!isSubmitted && userSlice.loading ? (
            <ContainerSpinner />
          ) : !isSubmitted && userSlice.error ? (
            <>
              <h4>Link Inválido:</h4>
              <p>
                O link de acesso a redefinição de senha não existe mais. Para redefinir a sua senha, clique em redefinir
                senha na tela de login.
              </p>

              <Button onClick={goToHome}>Voltar para Login</Button>
            </>
          ) : (
            <>
              <h4>Redefinir Senha:</h4>

              <Form onSubmit={handleSubmit}>
                <Form.Group controlId="newPassword">
                  <Form.Floating className="mb-3">
                    <Form.Label>Nova Senha</Form.Label>
                    <InputGroup>
                      <Form.Control
                        required
                        type={isPasswordShown ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                      />
                      <IconButton
                        className="form-control-icon"
                        style={{ padding: 0 }}
                        onClick={onChangeIsPasswordShown}
                      >
                        {isPasswordShown ? <EyeIcon /> : <EyeSlashIcon />}
                      </IconButton>
                    </InputGroup>
                  </Form.Floating>
                </Form.Group>

                <Form.Group controlId="reNewPassword" className="mb-3">
                  <Form.Floating>
                    <Form.Label>Repetir Senha</Form.Label>
                    <InputGroup>
                      <Form.Control
                        required
                        type={isPasswordShown ? "text" : "password"}
                        value={reNewPassword}
                        onChange={(e) => setReNewPassword(e.target.value)}
                      />
                      <IconButton
                        className="form-control-icon"
                        style={{ padding: 0 }}
                        onClick={onChangeIsPasswordShown}
                      >
                        {isPasswordShown ? <EyeIcon /> : <EyeSlashIcon />}
                      </IconButton>
                    </InputGroup>
                  </Form.Floating>
                </Form.Group>

                <ButtonSpinner type="submit" isLoading={userSlice.loading}>
                  Redefinir Senha
                </ButtonSpinner>
              </Form>
            </>
          )}
        </div>

        {isSubmitted && userSlice.error && <FormAlert alertTextList={userSlice.error} />}
      </AuthenticationLayout>
    </>
  );
}

export default PasswordResetConfirmScreen;
